import { Link } from "react-router-dom";
import { useState } from "react";
import { FlaskConical, Mail, MapPin, ShieldCheck, Microscope } from "lucide-react";
import { apiClient } from "@/lib/api";
import ScientificPattern from "@/components/ScientificPattern";

const COLS = [
  {
    title: "Catalog",
    links: [
      { to: "/shop", label: "All Research Compounds" },
      { to: "/shop?category=glp-1-research", label: "GLP-1 Research" },
      { to: "/shop?category=healing-recovery-research", label: "Healing & Recovery Research" },
      { to: "/shop?category=growth-hormone-research", label: "Growth Hormone Research" },
      { to: "/shop?category=cellular-longevity-research", label: "Cellular & Longevity Research" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About Us" },
      { to: "/lab-testing", label: "Laboratory Testing" },
      // { to: "/coa-library", label: "COA Library" }, // HIDDEN: uncomment to restore
      // { to: "/research", label: "Research Library" }, // HIDDEN: uncomment to restore
      { to: "/contact", label: "Contact" },
      { to: "/contact", label: "Bulk / Institutional Inquiries" },
      { to: "/affiliate-program", label: "Affiliate Program" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/privacy-policy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/shipping-policy", label: "Shipping Policy" },
      { to: "/returns-policy", label: "Returns Policy" },
      { to: "/research-use-disclaimer", label: "Research Use Only Disclaimer" },
    ],
  },
];

const SOCIALS = [];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/newsletter", { email });
      setStatus("Subscribed successfully.");
      setEmail("");
    } catch {
      setStatus("Something went wrong. Please try again.");
    }
  };

  return (
    <footer className="no-print relative bg-slate-950 text-slate-300 overflow-hidden" data-testid="main-footer">
      <div className="h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
      <ScientificPattern variant="grid" className="opacity-[0.4]" />
      <div className="absolute -top-24 right-0 h-72 w-72 bg-blue-600/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <FlaskConical className="h-6 w-6 text-brandblue-400" strokeWidth={1.5} />
            <span className="font-display font-bold text-white text-lg">True Impact Peptides</span>
          </div>
          <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
            Advancing Research Through Precision. Premium Research Use Only compounds manufactured to rigorous quality standards with comprehensive batch documentation.
          </p>

          <div className="mt-6 flex items-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-brandblue-400" /> COA Verified</span>
            <span className="flex items-center gap-1.5"><Microscope className="h-3.5 w-3.5 text-brandblue-400" /> Lab Certified</span>
          </div>

          <form onSubmit={subscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              placeholder="Research email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 rounded-full bg-slate-900 border border-slate-700 px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              data-testid="footer-newsletter-input"
            />
            <button type="submit" className="rounded-full bg-gradient-to-r from-blue-900 to-blue-500 hover:shadow-blue-glow hover:-translate-y-0.5 px-4 py-2 text-sm font-medium text-white transition-all duration-300" data-testid="footer-newsletter-submit">
              <Mail className="h-4 w-4" />
            </button>
          </form>
          {status && <p className="text-xs text-brandblue-400 mt-2" data-testid="footer-newsletter-status">{status}</p>}

          <div className="mt-6 flex items-center gap-3">
            {SOCIALS.map((s) => (
              <a key={s.label} href="#" aria-label={s.label} className="h-8 w-8 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500 hover:shadow-blue-glow-sm transition-all duration-300" data-testid={`footer-social-${s.label.toLowerCase()}`}>
                <s.icon className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
        </div>

        {COLS.map((col) => (
          <div key={col.title}>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">{col.title}</h4>
            <ul className="space-y-2.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-slate-400 hover:text-blue-400 transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="relative border-t border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} True Impact Peptides. All rights reserved.</p>
          <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> For laboratory and in-vitro research use only. Not for human or veterinary consumption.</p>
        </div>
      </div>
    </footer>
  );
}
